/**
 * HTTP client wrappers for the secure download endpoints.
 *
 *   POST /api/chat/download-soql        — re-runs a SOQL query
 *   POST /api/chat/download-tool-result — re-runs a non-SOQL MCP tool
 *
 * Both require {session_uuid, message_uuid} in the body — the server uses
 * those to validate that the caller actually owns the message before running
 * anything. The server also verifies the (tool, args) in the body match what
 * was actually executed for that message, so users can't POST arbitrary SOQL.
 *
 * Returns a Blob + filename pair. The caller (useDownloadAction) is responsible
 * for invoking file-saver to actually save the file.
 */
import { createAxiosInstance } from "@/dataProvider/axios";

import { DEV_AGENT_BASE_URL, devAgentAuthHeaders } from "../devAgentService";

export type ExportFormat = "csv" | "txt" | "pdf" | "docx";

export interface DownloadCorrelation {
  sessionUuid: string;
  messageUuid: string;
}

// Dedicated axios instance with a long timeout — large orgs can take minutes
// to paginate through every record. Uses createAxiosInstance so it inherits
// the same auth path (GCP OIDC via /proxy/dev-agent) as the rest of the dev
// agent service — a raw axios.create bypasses the proxy and 401s on dev.
const downloadApi = createAxiosInstance(DEV_AGENT_BASE_URL);
downloadApi.defaults.timeout = 600_000;

// Rewrite axios error.message to a user-friendly string so the download
// toast shows readable text instead of "Request failed with status code 404".
// Mirrors the global interceptor in dataProvider/axios/index.ts — kept inline
// here because this dedicated client deliberately bypasses that instance.
downloadApi.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    try {
      const status = error.response?.status;
      // Body comes back as a Blob when responseType is "blob" — read it as
      // text + JSON-parse to surface the FastAPI `detail` field.
      let backendDetail: string | null = null;
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          const parsed = JSON.parse(txt);
          backendDetail = parsed?.detail || parsed?.message || null;
        } catch { /* not JSON */ }
      } else if (data && typeof data === "object") {
        backendDetail = data.detail || data.message || null;
      }
      let friendly: string;
      if (backendDetail) friendly = backendDetail;
      else if (status === 404) friendly = "Sorry, we couldn't find that data";
      else if (status === 403) friendly = "Sorry, you don't have access to this";
      else if (status === 408 || error.code === "ECONNABORTED") friendly = "Download timed out — try again";
      else if (status && status >= 500) friendly = "The server isn't responding right now";
      else if (!status) friendly = "Sorry, couldn't reach the server";
      else friendly = "Sorry, the download didn't work";
      (error as any).rawMessage = error.message;
      error.message = friendly;
    } catch { /* never let error formatting throw */ }
    return Promise.reject(error);
  },
);

/** Parse a filename out of a Content-Disposition header. Plain string ops, no regex. */
const filenameFromHeader = (cd: string, fallback: string): string => {
  const idx = cd.indexOf("filename=");
  if (idx < 0) return fallback;
  let value = cd.slice(idx + "filename=".length).trim();
  if (value.startsWith('"')) value = value.slice(1);
  const endQuote = value.indexOf('"');
  if (endQuote >= 0) value = value.slice(0, endQuote);
  return value || fallback;
};

const extractContentDisposition = (headers: Record<string, unknown>): string => {
  const v = headers["content-disposition"] || headers["Content-Disposition"];
  return typeof v === "string" ? v : "";
};

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

const fallbackName = (format: ExportFormat): string =>
  `export_${Date.now()}.${format}`;

/** Run a SOQL query on the server and stream the FULL result in the chosen format. */
export const downloadSoqlAs = async (
  query: string,
  format: ExportFormat,
  corr: DownloadCorrelation,
  opts: { tooling?: boolean; title?: string } = {},
): Promise<DownloadResult> => {
  const resp = await downloadApi.post(
    "/api/chat/download-soql",
    {
      session_uuid: corr.sessionUuid,
      message_uuid: corr.messageUuid,
      query,
      format,
      tooling: opts.tooling || false,
      title: opts.title,
    },
    { responseType: "blob", headers: devAgentAuthHeaders() },
  );
  return {
    blob: resp.data as Blob,
    filename: filenameFromHeader(extractContentDisposition(resp.headers), fallbackName(format)),
  };
};

/** Re-run a non-SOQL MCP tool (list_metadata, graph_*, etc.) on the server. */
export const downloadToolResultAs = async (
  tool: string,
  args: Record<string, unknown>,
  format: ExportFormat,
  corr: DownloadCorrelation,
  opts: { title?: string } = {},
): Promise<DownloadResult> => {
  const resp = await downloadApi.post(
    "/api/chat/download-tool-result",
    {
      session_uuid: corr.sessionUuid,
      message_uuid: corr.messageUuid,
      tool,
      args,
      format,
      title: opts.title,
    },
    { responseType: "blob", headers: devAgentAuthHeaders() },
  );
  return {
    blob: resp.data as Blob,
    filename: filenameFromHeader(extractContentDisposition(resp.headers), fallbackName(format)),
  };
};

/** Fetch tool result as CSV text → parse into rows (max 200). For inline table view. */
export const fetchToolResultAsRows = async (
  tool: string,
  args: Record<string, unknown>,
  corr: DownloadCorrelation,
  isSoql: boolean,
): Promise<Record<string, string>[]> => {
  const endpoint = isSoql ? "/api/chat/download-soql" : "/api/chat/download-tool-result";
  const body = isSoql
    ? { session_uuid: corr.sessionUuid, message_uuid: corr.messageUuid, query: (args as any).query, format: "csv", tooling: (args as any).tooling || false }
    : { session_uuid: corr.sessionUuid, message_uuid: corr.messageUuid, tool, args, format: "csv" };
  const resp = await downloadApi.post(endpoint, body, {
    responseType: "text",
    headers: devAgentAuthHeaders(),
  });
  const text = typeof resp.data === "string" ? resp.data : await (resp.data as Blob).text();
  const lines = text.split("\n").filter((l: string) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h: string) => h.replace(/^"|"$/g, "").trim());
  return lines.slice(1, 201).map((line: string) => {
    const vals = line.split(",").map((v: string) => v.replace(/^"|"$/g, "").trim());
    const row: Record<string, string> = {};
    headers.forEach((h: string, i: number) => { row[h] = vals[i] || ""; });
    return row;
  });
};
